/**
 * Marketing process strip — short animated journey chips (Discovery → Export).
 * Soft “wow” without heavy deps; respects prefers-reduced-motion via CSS.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

type ProcessJourneyStripProps = {
  /** Compact = landing mid-page; full = process page hero band */
  variant?: 'compact' | 'full';
  className?: string;
};

const ProcessJourneyStrip: React.FC<ProcessJourneyStripProps> = ({
  variant = 'compact',
  className = '',
}) => {
  const { t } = useTranslation();
  const steps = t('pages.landing.journey_steps', { returnObjects: true }) as string[];
  const labels = Array.isArray(steps) ? steps : [];

  if (labels.length === 0) return null;

  return (
    <section
      className={`marketing-journey ${variant === 'full' ? 'marketing-journey--full' : ''} ${className}`}
      aria-labelledby="marketing-journey-heading"
    >
      <div className="mx-auto max-w-5xl">
        <h2
          id="marketing-journey-heading"
          className="text-lg sm:text-xl font-bold text-white text-center font-poppins"
        >
          {t('pages.landing.journey_title')}
        </h2>
        <p className="mt-2 text-sm text-gray-400 text-center max-w-2xl mx-auto">
          {t('pages.landing.journey_lead')}
        </p>

        <ol className="marketing-journey-track mt-8 sm:mt-10">
          {labels.map((label, index) => (
            <li key={`${label}-${index}`} className="marketing-journey-step">
              <span className="marketing-journey-index" aria-hidden>
                {index + 1}
              </span>
              <span className="marketing-journey-label">{label}</span>
              {index < labels.length - 1 ? (
                <ArrowRight
                  className="marketing-journey-arrow hidden sm:block"
                  aria-hidden
                />
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default ProcessJourneyStrip;
